/**
 * Who sees what (claude-docs/2026-09-26-books-desk-phase3-design.md §3,
 * phase 3d §3): ONE predicate and ONE hook. Every screen and the sidebar
 * read these; nothing else decides.
 *
 *   contractor  the desk: the books are in this build AND the account is
 *               EITHER one an admin marked (`users/{uid}.accountKind ===
 *               'contractor'`) OR a member of an organization (the `orgId`
 *               claim the books' claim sync stamps on the sign-in). A
 *               member's ROLE still decides what they see inside.
 *   money       a contractor whose books organization has the money plan
 *               (`organizations.money_enabled`) — billing, costs, progress
 *   mayStartOrganization
 *               only the admin mark: what lets the desk CREATE an
 *               organization for someone with no membership. A member who
 *               was never marked (and has since been removed, while their
 *               sign-in still carries the old claim) is never handed one.
 *   adminView   an admin's read-only view of one organization's desk
 *               (phase 3d §7, `adminView.js`): the view this tab keeps,
 *               honoured only for the admin who opened it — the `admin`
 *               claim the database checks, and the same uid — else null.
 *               It lets an admin who is no member into the desk and puts
 *               the viewed organization's plan in `money`; outside the
 *               desk it changes nothing (`contractor` stays theirs).
 *   mayUseAssistant
 *               the logo menu's assistant: an admin (the claim), or a
 *               contractor by the definition above — marked or a member —
 *               but WITHOUT the books build flag (`isContractorAccount`, the
 *               rule the `chat` callable refuses everyone else by).
 *   mayOpenAdminView
 *               the admin claim, in a build with the books: who may open
 *               such a view (pages/AdminViewPage.js). Anyone else following
 *               the link is sent to their own desk.
 *
 * The facts live in three places, read by two things that already read
 * them: `PrivateRoute` reads the user doc and the sign-in's claims on every
 * private page, and the desk shell's books session reads the organization.
 * Each supplies its half through `EntitlementsProvider`; outside the desk
 * the organization is not known, so `money` is false there (nothing outside
 * the desk asks).
 */
import React, { createContext, useContext, useMemo } from 'react';
import { readAdminView } from './adminView';
import { booksEnabled } from './flags';
import { isContractorAccount } from '@shared-lib/org-access';

export const isContractor = (userDoc) => userDoc?.accountKind === 'contractor';

/**
 * A member of a books organization, by the sign-in's `orgId` claim (the
 * claim sync, functions/features/books-claims.js) — the same claim the
 * Firestore rules admit a member's projects on.
 */
export const isTeamMember = (claims) =>
  typeof claims?.orgId === 'string' && claims.orgId !== '';

/**
 * The synced `admin` claim — a JSON true, as the books database reads it
 * (`current_is_admin`); a string that says so is not one.
 */
export const isAdmin = (claims) => claims?.admin === true;

// the kept view, for the admin who opened it (the look was logged for them)
const honouredView = (claims, view) =>
  view && isAdmin(claims) && view.uid === claims.sub ? view : null;

export const entitlementsFor = ({
  userDoc,
  claims,
  organization,
  booksOn,
  adminView = null,
}) => {
  const books = Boolean(booksOn);
  const mayStartOrganization = books && isContractor(userDoc);
  const contractor = mayStartOrganization || (books && isTeamMember(claims));
  const view = books ? honouredView(claims, adminView) : null;
  return {
    contractor,
    money:
      (contractor || view !== null) && organization?.money_enabled === true,
    mayStartOrganization,
    mayUseAssistant: isAdmin(claims) || isContractorAccount(userDoc, claims),
    adminView: view,
    mayOpenAdminView: books && isAdmin(claims),
  };
};

const EntitlementsContext = createContext({
  userDoc: null,
  claims: null,
  organization: null,
});

const inherit = (given, parent) => (given === undefined ? parent : given);

/** Supplies whichever part it is given; the rest is inherited. */
export function EntitlementsProvider({
  userDoc,
  claims,
  organization,
  children,
}) {
  const parent = useContext(EntitlementsContext);
  const value = useMemo(
    () => ({
      userDoc: inherit(userDoc, parent.userDoc),
      claims: inherit(claims, parent.claims),
      organization: inherit(organization, parent.organization),
    }),
    [parent, userDoc, claims, organization]
  );
  return (
    <EntitlementsContext.Provider value={value}>
      {children}
    </EntitlementsContext.Provider>
  );
}

export function useEntitlements() {
  const { userDoc, claims, organization } = useContext(EntitlementsContext);
  return entitlementsFor({
    userDoc,
    claims,
    organization,
    booksOn: booksEnabled(),
    adminView: readAdminView(),
  });
}
